import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type UseModalProps,
} from "@chakra-ui/react";
import {
  DropZone,
  FormRow,
  InputDatePicker,
  Select,
  TextField,
} from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { MediaList, MediaModel } from "@src/utils/components/dropzone";
import { now } from "@src/utils/dates";
import {
  fieldToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { arrayLengthMin, required } from "@src/utils/forms/validators";
import { FormMode } from "@src/utils/types";
import { ContractorSelect } from "@src/widgets/ContractorSelect";
import { pull } from "lodash";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type FunctionComponent, useContext, useEffect } from "react";
import { DocumentsCtx } from "../../StoreDocuments";
import { useOptions } from "./Common";
import type { IFormContractSerialized } from "./FormContract";
import { RelatedTo, type StoreContractForm } from "./StoreContractForm";

export const ModalEditContractForm: FunctionComponent<
  React.PropsWithChildren<
    UseModalProps & {
      title?: React.ReactNode;
      loading?: boolean;
      primaryActionContent: string;
      onSubmit(arg: IFormContractSerialized): void;
      store: StoreContractForm;
      mode: FormMode;
      defaultContractorName?: string;
      prefillFormFields?: {
        projectId?: string[];
        brandId?: string;
        clientId?: string;
        contractorId?: string;
      };
    }
  >
> = observer(function ModalEditContractForm({
  title,
  primaryActionContent,
  isOpen,
  mode,
  loading,
  onClose,
  onSubmit,
  store,
  prefillFormFields,
  defaultContractorName,
}) {
  const documentsStore = useContext(DocumentsCtx);
  useOptions(store, documentsStore);

  useEffect(() => {
    if (prefillFormFields?.brandId) {
      store.form.related_to.onChange(RelatedTo.Brand);
      store.form.brand_id.onChange(prefillFormFields?.brandId);
    }
    if (prefillFormFields?.projectId) {
      store.form.related_to.onChange(RelatedTo.Projects);
      store.form.project_ids.onChange(prefillFormFields?.projectId);
    }
    if (prefillFormFields?.clientId) {
      store.form.related_to.onChange(RelatedTo.Client);
      store.form.client_id.onChange(prefillFormFields?.clientId);
    }
    if (prefillFormFields?.contractorId) {
      store.form.related_to.onChange(RelatedTo.Contractor);
      store.form.contractor_id.onChange(prefillFormFields?.contractorId);
    }
  }, [prefillFormFields]);

  useEffect(() => {
    store.form.related_to.$ === RelatedTo.Client
      ? store.form.client_id.validators(required)
      : store.form.client_id.validators();

    store.form.related_to.$ === RelatedTo.Brand
      ? store.form.brand_id.validators(required)
      : store.form.brand_id.validators();

    store.form.related_to.$ === RelatedTo.Contractor
      ? store.form.contractor_id.validators(required)
      : store.form.contractor_id.validators();

    store.form.related_to.$ === RelatedTo.Projects
      ? store.form.project_ids.validators(arrayLengthMin(1))
      : store.form.project_ids.validators();
  }, [store.form.related_to.$]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" trapFocus={false}>
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormRow title={<Trans>Type</Trans>}>
            <Select
              asPortal
              id="field-contract-country"
              label={""}
              {...fieldToInputProps(store.form.type)}
              options={store.typeOptions}
              placeholder={t`Choose type`}
            />
          </FormRow>
          <FormRow title={<Trans>Title</Trans>}>
            <TextField
              id="field-contract-title"
              {...fieldToInputProps(store.form.title)}
              type="text"
            />
          </FormRow>

          <FormRow title={<Trans>Valid from</Trans>}>
            <div className="w-56">
              <InputDatePicker
                disableDatesAfter={store.form.valid_to.$}
                error={store.form.valid_from.error}
                selected={store.form.valid_from.$}
                onChange={(val) => {
                  if (val) {
                    store.form.valid_from.onChange(val.start);
                  }
                }}
              />
            </div>
          </FormRow>

          <FormRow title={<Trans>Valid to</Trans>}>
            <div className="w-56">
              <InputDatePicker
                disableDatesBefore={store.form.valid_from.$}
                error={store.form.valid_to.error}
                selected={store.form.valid_to.$}
                isDisabled={store.form.valid_to_indefinite.value}
                onChange={(val) => {
                  store.form.valid_to.onChange(val?.start);
                }}
              />
            </div>
            <div className="flex items-center mt-3">
              <Checkbox
                id="field-contract-indefinite"
                isChecked={store.form.valid_to_indefinite.value}
                onChange={(event) => {
                  store.form.valid_to_indefinite.onChange(event.target.checked);
                  if (event.target.checked) {
                    store.form.valid_to.onChange(undefined);
                  } else {
                    store.form.valid_to.onChange(now());
                  }
                }}
              >
                <Trans>Indefinite</Trans>
              </Checkbox>
            </div>
          </FormRow>

          <FormRow title={<Trans>Related to</Trans>}>
            <Select
              asPortal
              id="field-contract-related"
              label={""}
              {...fieldToInputProps(store.form.related_to)}
              options={store.relatedToOptions}
              placeholder={t`Level of relation`}
              onChange={(val) => {
                if (val !== RelatedTo.Projects) {
                  store.form.project_ids.reset();
                }

                if (val !== RelatedTo.Client) {
                  store.form.client_id.reset();
                }

                if (val !== RelatedTo.Brand) {
                  store.form.brand_id.reset();
                }

                if (val !== RelatedTo.Contractor) {
                  store.form.contractor_id.reset();
                }
                store.form.related_to.onChange(val);
              }}
            />
          </FormRow>
          {store.form.form.$.related_to.$ === RelatedTo.Projects && (
            <FormRow title={<Trans>Projects</Trans>}>
              <div>
                <Select
                  asPortal
                  isMulti
                  id="create-rfi-projecs-select"
                  placeholder={t`Projects`}
                  {...fieldToSelectProps(
                    store.form.project_ids,
                    store.projectOptions,
                  )}
                />
              </div>
            </FormRow>
          )}
          {store.form.form.$.related_to.$ === RelatedTo.Client && (
            <FormRow title={<Trans>Client</Trans>}>
              <Select
                asPortal
                id="field-contract-client"
                label={""}
                {...fieldToInputProps(store.form.client_id)}
                isDisabled={store.form.form.$.related_to.$ !== RelatedTo.Client}
                options={store.clientOptions}
                placeholder={t`Client`}
              />
            </FormRow>
          )}
          {store.form.form.$.related_to.$ === RelatedTo.Brand && (
            <FormRow title={<Trans>Brand</Trans>}>
              <Select
                asPortal
                id="field-contract-brand"
                label={""}
                isDisabled={store.form.form.$.related_to.$ !== RelatedTo.Brand}
                {...fieldToInputProps(store.form.brand_id)}
                options={store.brandOptions}
                placeholder={t`Brand`}
              />
            </FormRow>
          )}
          {store.form.form.$.related_to.$ === RelatedTo.Contractor && (
            <FormRow title={<Trans>Contractor</Trans>}>
              <ContractorSelect
                defaultName={defaultContractorName}
                field={store.form.contractor_id}
                waitForDefaultName={mode === FormMode.EDIT}
              />
            </FormRow>
          )}

          <DropZone
            onDrop={(files) => {
              store.form.files.onChange([
                ...store.form.files.$,
                ...files.map((f) => new MediaModel(f)),
              ]);
            }}
            allowMultiple={true}
            accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/docx, application/pdf, text/plain, application/msword, image/jpeg, image/png"
          />
          <MediaList
            files={store.form.files.$}
            onDelete={(val) => {
              pull(store.form.files.$, val);
            }}
          />
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="grey" onClick={onClose} variant="outline">
            <Trans>Cancel</Trans>
          </Button>
          <Button
            ml={3}
            isLoading={loading}
            onClick={async () => {
              const data = await store.form.validate();

              if (data === null) return console.log(store.form.form.error);

              onSubmit(data);
            }}
          >
            {primaryActionContent}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
